import type { EnvProp, PathDef, V2 } from './types';

export type ZoneProp = Extract<EnvProp, { type: 'zone' }>;

const DEG = Math.PI / 180;

/** ¿Está el punto (x,z) dentro de la zona (rectángulo rotado)? */
export function pointInZone(zone: ZoneProp, x: number, z: number, margin = 0): boolean {
  const rot = -(zone.rotYDeg ?? 0) * DEG;
  const dx = x - zone.pos[0];
  const dz = z - zone.pos[1];
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const lx = dx * cos - dz * sin;
  const lz = dx * sin + dz * cos;
  return (
    Math.abs(lx) <= zone.size[0] / 2 + margin && Math.abs(lz) <= zone.size[1] / 2 + margin
  );
}

export interface PathProjection {
  /** Distancia lateral absoluta al eje de la trayectoria (m). */
  lateralM: number;
  /** Progreso acumulado a lo largo de la trayectoria (m). */
  progressM: number;
}

export function pathLength(path: PathDef): number {
  let len = 0;
  for (let i = 1; i < path.points.length; i++) {
    const [ax, az] = path.points[i - 1]!;
    const [bx, bz] = path.points[i]!;
    len += Math.hypot(bx - ax, bz - az);
  }
  return len;
}

/** Proyección del punto sobre la polilínea: lateral y progreso del punto más cercano. */
export function projectOnPath(path: PathDef, x: number, z: number): PathProjection {
  let best = Number.POSITIVE_INFINITY;
  let bestProgress = 0;
  let acc = 0;
  for (let i = 1; i < path.points.length; i++) {
    const [ax, az] = path.points[i - 1]!;
    const [bx, bz] = path.points[i]!;
    const vx = bx - ax;
    const vz = bz - az;
    const segLen2 = vx * vx + vz * vz;
    const segLen = Math.sqrt(segLen2);
    let t = 0;
    if (segLen2 > 1e-9) t = Math.min(1, Math.max(0, ((x - ax) * vx + (z - az) * vz) / segLen2));
    const px = ax + vx * t;
    const pz = az + vz * t;
    const dist = Math.hypot(x - px, z - pz);
    if (dist < best) {
      best = dist;
      bestProgress = acc + segLen * t;
    }
    acc += segLen;
  }
  return { lateralM: best, progressM: bestProgress };
}

/** Genera los puntos de un rectángulo redondeado (para circuitos). CCW desde (-hx, 0). */
export function roundedRectPath(hx: number, hz: number, r: number, segsPerCorner = 6): V2[] {
  const pts: V2[] = [];
  const corners: Array<{ c: V2; from: number; to: number }> = [
    { c: [-(hx - r), hz - r], from: 180, to: 90 }, // esquina superior izquierda
    { c: [hx - r, hz - r], from: 90, to: 0 }, // superior derecha
    { c: [hx - r, -(hz - r)], from: 0, to: -90 }, // inferior derecha
    { c: [-(hx - r), -(hz - r)], from: -90, to: -180 }, // inferior izquierda
  ];
  pts.push([-hx, 0]);
  for (const { c, from, to } of corners) {
    for (let s = 0; s <= segsPerCorner; s++) {
      const a = (from + ((to - from) * s) / segsPerCorner) * DEG;
      pts.push([c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r]);
    }
  }
  pts.push([-hx, 0]);
  return pts;
}
